import {
  PAGE_TYPE,
  getCategoryPageTypeConfig,
} from 'components/consumer/category-page-algolia/categoryPageTypes';
import { ALGOLIA_ANALYTICS_TAG, SALE_PAGE_PATHS } from './constants';

/**
 * Normalises a pathname to a bare slug path: no origin, no query, no hash, no
 * leading or trailing slashes, lowercased. `/Clearance/?foo=1` -> `clearance`,
 * `/` -> `''`.
 */
export const normalizePathname = pathname => {
  if (typeof pathname !== 'string') {
    return '';
  }

  return pathname
    .split('?')[0]
    .split('#')[0]
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();
};

// Listing page type -> tag. Covers every PAGE_TYPE, including those with no
// route kind of their own (collection, new arrivals).
const TAG_BY_PAGE_TYPE = {
  [PAGE_TYPE.CATEGORY]: ALGOLIA_ANALYTICS_TAG.CATEGORY_PAGE,
  [PAGE_TYPE.COLLECTION]: ALGOLIA_ANALYTICS_TAG.CATEGORY_PAGE,
  [PAGE_TYPE.NEW_ARRIVALS]: ALGOLIA_ANALYTICS_TAG.CATEGORY_PAGE,
  // The ticket puts Ready to Ship under category_page: "To include /instock".
  [PAGE_TYPE.READY_TO_SHIP]: ALGOLIA_ANALYTICS_TAG.CATEGORY_PAGE,
  [PAGE_TYPE.CLEARANCE]: ALGOLIA_ANALYTICS_TAG.CLEARANCE_PAGE,
  [PAGE_TYPE.FLASH_SALE]: ALGOLIA_ANALYTICS_TAG.FLASH_SALE_PAGE,
};

export const getAnalyticsTagForPageType = pageType =>
  TAG_BY_PAGE_TYPE[pageType] ?? null;

// Paths that identify their own page type. Checked before the route kind so
// that the homepage and /search/ — neither of which goes through the catch-all
// classifier — are tagged without any server plumbing.
const TAG_BY_PATH = new Map([
  ['', ALGOLIA_ANALYTICS_TAG.HOMEPAGE],
  ['search', ALGOLIA_ANALYTICS_TAG.SEARCH_RESULTS_PAGE],
  ...[
    PAGE_TYPE.CLEARANCE,
    PAGE_TYPE.FLASH_SALE,
    PAGE_TYPE.READY_TO_SHIP,
  ].map(pageType => [
    getCategoryPageTypeConfig(pageType).pageKey,
    getAnalyticsTagForPageType(pageType),
  ]),
  ...SALE_PAGE_PATHS.map(path => [path, ALGOLIA_ANALYTICS_TAG.SALE_PAGE]),
]);

// Route kinds as returned by the consumer app's getCatchAllRouteKind.
const TAG_BY_ROUTE_KIND = {
  pdp: ALGOLIA_ANALYTICS_TAG.PDP,
  category: ALGOLIA_ANALYTICS_TAG.CATEGORY_PAGE,
  stylePlp: ALGOLIA_ANALYTICS_TAG.CATEGORY_PAGE,
  readyToShip: getAnalyticsTagForPageType(PAGE_TYPE.READY_TO_SHIP),
  flashSale: getAnalyticsTagForPageType(PAGE_TYPE.FLASH_SALE),
  clearance: getAnalyticsTagForPageType(PAGE_TYPE.CLEARANCE),
};

/**
 * Resolves the `analyticsTags` value for the page a search is being made from.
 *
 * The path alone cannot separate a PDP from a category: `/sofas/hughes-sofa/`
 * is a product and `/modern/desks/` is a category, and both are served by the
 * same CMS `:category/:slug` route. That distinction needs CMS route metadata,
 * which only the server has, so it arrives as `routeKind` (the output of the
 * consumer app's getCatchAllRouteKind).
 *
 * `routeKindPath` is the path the route kind was resolved for. On a soft
 * navigation the new pathname and the new route kind are two separate writes
 * landing in the same commit; without this guard a stale kind would tag the
 * first render of a PDP as a category page. A mismatch drops the kind rather
 * than trusting it.
 *
 * @returns {string|null} A tag, or null for pages this ticket does not name
 *   (CMS content, blog, cart, checkout ...), which are left untagged.
 */
const getPageAnalyticsTag = ({ pathname, routeKind, routeKindPath } = {}) => {
  // A missing pathname is unknown, not the homepage — `''` is only the
  // homepage when it was actually read off a location.
  if (typeof pathname !== 'string') {
    return null;
  }

  const path = normalizePathname(pathname);

  const tagForPath = TAG_BY_PATH.get(path);
  if (tagForPath) {
    return tagForPath;
  }

  if (!routeKind || normalizePathname(routeKindPath) !== path) {
    return null;
  }

  return TAG_BY_ROUTE_KIND[routeKind] ?? null;
};

export default getPageAnalyticsTag;
